/**
 * Structured worlds + learning paths for Topics / World Explorer.
 * Keys align with home explore ids: animals, robotics, math, space.
 */
export const WORLD_EXPLORER_BY_ID = {
  animals: {
    id: 'animals',
    title: 'Animals',
    icon: '🦁',
    bannerGradient:
      'linear-gradient(120deg, rgba(34, 197, 94, 0.45) 0%, rgba(16, 185, 129, 0.28) 42%, rgba(6, 95, 70, 0.15) 100%)',
    description:
      'From tiny insects to ocean giants — explore how living things survive, cooperate, and shape their habitats.',
    ageRangeLabel: 'Ages 5–12',
    overallProgress: 14,
    learningPaths: [
      {
        id: 'animal-habitats',
        title: 'Animal Habitats',
        blurb: 'Match animals to the homes and conditions where they thrive.',
        difficulty: 'Beginner',
        ageBand: '5-8',
        durationMinutes: 7,
        progress: 10,
        adventureQuery: 'animal habitats for kids how forests deserts work',
      },
      {
        id: 'what-do-animals-eat',
        title: 'What Do Animals Eat?',
        blurb: 'Herbivores, carnivores, and omnivores — how diets differ in nature.',
        difficulty: 'Beginner',
        ageBand: '5-8',
        durationMinutes: 12,
        progress: 0,
        adventureQuery: 'herbivore carnivore omnivore for kids simple',
      },
      {
        id: 'animal-adaptations',
        title: 'Animal Adaptations',
        blurb: 'Camouflage, migration, and body tools that help species survive.',
        difficulty: 'Intermediate',
        ageBand: '9-12',
        durationMinutes: 16,
        progress: 0,
        adventureQuery: 'animal adaptations for kids camouflage migration',
      },
    ],
  },
  robotics: {
    id: 'robotics',
    title: 'Robotics',
    icon: '🤖',
    bannerGradient:
      'linear-gradient(120deg, rgba(99, 102, 241, 0.4) 0%, rgba(59, 130, 246, 0.3) 45%, rgba(30, 58, 138, 0.18) 100%)',
    description:
      'How robots sense, decide, and move — ideas from real engineering, explained for curious kids.',
    ageRangeLabel: 'Ages 5–12',
    overallProgress: 8,
    learningPaths: [
      {
        id: 'robot-path-sequencing',
        title: 'Robot Path Sequencing',
        blurb: 'Order the steps to map a path, calibrate sensors, and test a careful run.',
        difficulty: 'Beginner',
        ageBand: '5-8',
        durationMinutes: 7,
        progress: 0,
        adventureQuery: 'how robots follow a line path for kids',
      },
      {
        id: 'sensors-and-signals',
        title: 'Sensors and Signals',
        blurb: 'Robot inputs — distance, light, and touch. Opens after the first Robot Path lesson (ordering skills stay on Robot Path).',
        difficulty: 'Beginner',
        ageBand: '5-8',
        durationMinutes: 8,
        progress: 0,
        adventureQuery: 'robot sensors and signals simple explanation kids',
      },
      {
        id: 'gears-and-motion',
        title: 'Gears and Motion',
        blurb: 'Spin direction, gear ratios, and speed races — read meshing gears like a tiny engineer.',
        difficulty: 'Intermediate',
        ageBand: '9-12',
        durationMinutes: 7,
        progress: 0,
        adventureQuery: 'gear ratio rpm for kids how gears work',
      },
      {
        id: 'electricity-and-circuits',
        title: 'Electricity and Circuits',
        blurb: 'Wire LEDs, build circuit paths, and play with switch logic — a circuits-only adventure.',
        difficulty: 'Intermediate',
        ageBand: '9-12',
        durationMinutes: 7,
        progress: 0,
        adventureQuery: 'simple electric circuit for kids led battery switch',
      },
    ],
  },
  math: {
    id: 'math',
    title: 'Math Adventures',
    icon: '🔢',
    bannerGradient:
      'linear-gradient(120deg, rgba(249, 115, 22, 0.38) 0%, rgba(234, 179, 8, 0.28) 50%, rgba(180, 83, 9, 0.12) 100%)',
    description:
      'Patterns, shapes, and number stories that make math feel like discovery — not drill sheets.',
    ageRangeLabel: 'Ages 5–12',
    overallProgress: 22,
    learningPaths: [
      {
        id: 'number-patterns',
        title: 'Number Patterns',
        blurb: 'Spot the rule, extend the list — growing patterns in arithmetic.',
        difficulty: 'Beginner',
        ageBand: '5-8',
        durationMinutes: 7,
        progress: 15,
        adventureQuery: 'number patterns for kids fun examples',
      },
      {
        id: 'shape-detective',
        title: 'Shape Detective',
        blurb: 'Use clues about sides and corners to sort circles, triangles, and rectangles.',
        difficulty: 'Beginner',
        ageBand: '5-8',
        durationMinutes: 12,
        progress: 0,
        adventureQuery: 'shapes geometry for kids learn about',
      },
      {
        id: 'groups-and-arrays',
        title: 'Groups and Arrays',
        blurb: 'Line up equal rows, read arrays as “groups of,” and bridge to multiplication.',
        difficulty: 'Core',
        ageBand: '5-8',
        durationMinutes: 8,
        progress: 0,
        adventureQuery: 'arrays multiplication equal groups for kids',
      },
      {
        id: 'multiplication-adventure',
        title: 'Multiplication Adventure',
        blurb: 'Equal groups, skip counting, and what multiplication really means.',
        difficulty: 'Intermediate',
        ageBand: '9-12',
        durationMinutes: 14,
        progress: 0,
        adventureQuery: 'multiplication for kids skip counting groups',
      },
    ],
  },
  space: {
    id: 'space',
    title: 'Space Missions',
    icon: '🚀',
    bannerGradient:
      'linear-gradient(120deg, rgba(124, 58, 237, 0.42) 0%, rgba(14, 165, 233, 0.32) 48%, rgba(30, 27, 75, 0.2) 100%)',
    description:
      'Orbits, rovers, and radio signals — explore how we study planets and talk to machines far from Earth.',
    ageRangeLabel: 'Ages 5–12',
    overallProgress: 0,
    learningPaths: [
      {
        id: 'space-missions',
        title: 'Space Missions',
        blurb: 'Three short missions: moon orbits, rover signals, and a clue hunt to name a mystery planet.',
        difficulty: 'Beginner',
        ageBand: '5-8',
        durationMinutes: 7,
        progress: 0,
        adventureQuery: 'moon orbit and mars rover for kids how space missions work',
      },
    ],
  },
  pebbles: {
    id: 'pebbles',
    title: 'Heart',
    icon: '🩷',
    bannerGradient:
      'linear-gradient(120deg, rgba(244, 114, 182, 0.42) 0%, rgba(251, 191, 36, 0.28) 50%, rgba(217, 70, 239, 0.12) 100%)',
    description:
      'Kindness, feelings, and the courage to be a good friend.',
    ageRangeLabel: 'Ages 4–8',
    contentLane: 'Heart',
    overallProgress: 0,
    learningPaths: [
      {
        id: 'kindness-and-caring',
        title: 'Kindness and Caring',
        blurb: 'Help Pebbles notice when a friend needs help and choose what to do next.',
        difficulty: 'Beginner',
        ageBand: '4-6',
        durationMinutes: 9,
        progress: 0,
        adventureQuery: 'kindness for kids helping friends feelings',
      },
    ],
  },
};

export const WORLD_EXPLORER_IDS = ['animals', 'robotics', 'math', 'space', 'pebbles'];

export function getWorldExplorer(worldId) {
  if (!worldId) return null;
  return WORLD_EXPLORER_BY_ID[worldId] || null;
}
